import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import RenderForm from "../../../components/Forms/Form"
import { FormColumnConfig, MasterConfigs } from "../../../interfaces/entity";
import { FormInstance, Typography } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData } from "../../../actions/apiCall";
import { useDispatch } from "react-redux";

const { Text } = Typography;

interface BasicDetailsProps {
    onLoadingChange?: (loading: boolean) => void;
}

const BasicDetails = forwardRef((props: BasicDetailsProps, ref) => {
    const { onLoadingChange } = props;
    const [typeOfBusiness, setTypeOfBusiness] = React.useState<string>();
    const [form, setForm] = React.useState<FormInstance>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [vendorId, setVendorId] = React.useState<string>('');
    const [isDisabled, setDisabled] = React.useState<any>(false);
    const dispatch = useDispatch();

    const getQueryStep = () => {
        const params = new URLSearchParams(location.search);
        const vendorId = params.get('vendor')
        setVendorId(vendorId || '')
        return vendorId
    };

    useEffect(() => {
        if (vendorId) {
            getDoccuments()
        }
    }, [vendorId])

    const getDoccuments = async () => {
        try {
            const resData = await dispatch(getData(`/v1/vendor/${vendorId}`));
            if (resData) {
                setDisabled(resData.data.msaSigned || resData.data.ndaSigned)
            } else {
                setDisabled(false)
            }
        } catch (error) {
            console.error('Error fetching document:', error);
        }
    }

    useEffect(() => {
        getQueryStep()
    }, [location.search]);

    const getCompanyPan = () => {
        if (typeOfBusiness === 'soleProprietorship' || typeOfBusiness === 'others' || !typeOfBusiness) {
            return {}
        } else {
            return {
                label: 'Company PAN',
                name: 'pan',
                type: 'text',
                placeholder: 'Enter Pan Number',
                rules: [{ required: true, message: 'PAN is required' }],
                span: 8,
            }
        }
    }

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useImperativeHandle(ref, () => ({
        submitStep: () => {
            return new Promise((resolve, reject) => {
                submitPromiseRef.current = { resolve, reject };
                form.submit();
            });
        }
    }));

    const submitPromiseRef = React.useRef<{ resolve?: (value: any) => void; reject?: (reason?: any) => void }>({});

    const gstnRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    const basicProfiles: MasterConfigs = {
        api: () => "/v1/vendor",
        modalTitle: () => "Vendor Registration",
        afterSubmit: async (res: any) => {
            submitPromiseRef.current?.resolve?.(res);
            setLoading(false);
        },
        onError: (error: any) => {
            submitPromiseRef.current?.reject?.(error);
            setLoading(false);
        },
        getFormColumns: () => [
            {
                label: 'Vendor Name (Company Name)',
                name: 'vendorName',
                type: 'text',
                placeholder: 'Enter Vendor Name',
                span: 8,
                rules: [{ required: true, message: 'Vendor Name is required' }]
            },
            {
                label: 'Company Registration Number',
                name: 'companyRegistrationNumber',
                type: 'text',
                placeholder: 'Enter Company Registration Number',
                rules: [{ required: true, message: 'Company Registration Number is required' }],
                span: 8,
            },
            {
                label: 'Website URL',
                name: 'websiteUrl',
                type: 'text',
                placeholder: 'Enter Website URL',
                rules: [{ required: true, message: 'Website URL is required' },
                { type: 'url', message: 'Please enter a valid URL' }],
                span: 8,
            },
            {
                label: 'Type of Business',
                name: 'typeOfBusiness',
                type: 'select',
                placeholder: 'Select Type of Business',
                span: 8,
                selectProps: {
                    onChange: (value) => {
                        setTypeOfBusiness(value);
                    }
                },
                children: [
                    { name: 'Private Limited', value: 'privateLimited' },
                    { name: 'Public Limited', value: 'publicLimited' },
                    { name: 'Partnership', value: 'partnership' },
                    { name: 'Sole Proprietorship', value: 'soleProprietorship' },
                    { name: 'LLP', value: 'llp' },
                    { name: 'Others', value: 'others' }
                ],
                rules: [{ required: true, message: 'Type of Business is required' }]
            },
            {
                label: 'Company GSTN',
                name: 'gstn',
                type: 'text',
                placeholder: 'Enter GSTN (e.g. 22AAAAA0000A1Z5)',
                inputProps: {
                    maxLength: 15,
                },
                rules: [{ required: true, message: 'GSTN is required' }, {
                    pattern: gstnRegex,
                    message: 'Invalid GSTN format. Example: 22AAAAA0000A1Z5',
                }],
                span: 8,
            },
            getCompanyPan() as FormColumnConfig,
            {
                type: 'custom',
                formItem: () => <Text strong>Business Address</Text>,
                span: 24,
            },
            {
                label: 'Street Address',
                name: 'streetAddress',
                type: 'text',
                placeholder: 'Enter Street Address',
                rules: [{ required: true, message: 'Street Address is required' }],
                span: 8,
            },
            {
                label: 'City',
                name: 'city',
                type: 'text',
                placeholder: 'Enter City',
                rules: [{ required: true, message: 'City is required' }],
                span: 8,
            },
            {
                label: 'State',
                name: 'state',
                type: 'text',
                placeholder: 'Enter State',
                rules: [{ required: true, message: 'State is required' }],
                span: 8,
            },
            {
                label: 'Country',
                name: 'country',
                type: 'text',
                placeholder: 'Enter Country',
                rules: [{ required: true, message: 'Country is required' }],
                span: 8,
            },
            {
                label: 'Postal Code',
                name: 'postalCode',
                type: 'text',
                placeholder: 'Enter Postal Code',
                rules: [{ required: true, message: 'Postal Code is required' }],
            }
        ]
    }

    return <RenderForm config={basicProfiles} formInstance={(form) => setForm(form)}
        setLoading={(e) => setLoading(e)} id={vendorId} isView={isDisabled} />
})

export default BasicDetails